import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment as env } from '@env/environment';

export interface TaskCenterSummary {
  ActionRequiredCount: number;
  OpenWorkItemCount: number;
  InProgressWorkItemCount: number;
  HistoryWorkItemCount: number;
  ProcessCount: number;
  RunningCount: number;
  SucceededLast24Hours: number;
  FailedLast24Hours: number;
}

export interface AdminWorkItem {
  Id: number;
  ProcessExecutionId: string;
  DocumentImportId?: string;
  Title: string;
  Description: string;
  Status: number;
  Priority: number;
  RowVersion: string;
  OriginalFileName?: string;
  AttemptCount: number;
  EmploymentNumber?: number;
  DisplayDate?: string;
  ErrorMessage?: string;
}

export interface ProcessExecution {
  Id: string;
  ProcessType: string;
  Status: number;
  Summary?: string;
  CurrentStep?: string;
  AttemptCount: number;
  CreatedAt: string;
  CompletedAt?: string;
  ErrorMessage?: string;
}

export interface TaskCenterPage<T> {
  Items: T[];
  TotalCount: number;
  PageNumber: number;
  PageSize: number;
}

@Injectable({ providedIn: 'root' })
export class ProcessTaskCenterService {
  private readonly baseUrl = `${env.hostApi}admin/task-center`;

  constructor(private http: HttpClient) { }

  getSummary(): Observable<TaskCenterSummary> {
    return this.http.get<TaskCenterSummary>(`${this.baseUrl}/summary`);
  }

  getWorkItems(status: string): Observable<TaskCenterPage<AdminWorkItem>> {
    const params = new HttpParams().set('pageSize', '100').set('status', status);
    return this.http.get<TaskCenterPage<AdminWorkItem>>(`${this.baseUrl}/work-items`, { params });
  }

  getProcesses(status = ''): Observable<TaskCenterPage<ProcessExecution>> {
    let params = new HttpParams().set('pageSize', '100');
    if (status) params = params.set('status', status);
    return this.http.get<TaskCenterPage<ProcessExecution>>(`${this.baseUrl}/processes`, { params });
  }

  claim(item: AdminWorkItem): Observable<AdminWorkItem> {
    return this.http.post<AdminWorkItem>(`${this.baseUrl}/work-items/${item.Id}/claim`, { RowVersion: item.RowVersion });
  }

  retry(item: AdminWorkItem, employmentNumber?: number, displayDate?: string): Observable<AdminWorkItem> {
    return this.http.post<AdminWorkItem>(`${this.baseUrl}/work-items/${item.Id}/retry`, {
      RowVersion: item.RowVersion,
      EmploymentNumber: employmentNumber,
      DisplayDate: displayDate || undefined
    });
  }

  resolve(item: AdminWorkItem): Observable<AdminWorkItem> {
    return this.http.post<AdminWorkItem>(`${this.baseUrl}/work-items/${item.Id}/resolve`, { RowVersion: item.RowVersion });
  }

  dismiss(item: AdminWorkItem): Observable<AdminWorkItem> {
    return this.http.post<AdminWorkItem>(`${this.baseUrl}/work-items/${item.Id}/dismiss`, { RowVersion: item.RowVersion });
  }

  getSource(item: AdminWorkItem): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/work-items/${item.Id}/source`, { responseType: 'blob' });
  }
}
